import { MatOption } from '@angular/material/core';
import { focusMatOptionElement, getMatOptionHostElement } from './focus-mat-option.util';

export function getFocusedMatOptionIndex(options: MatOption[]): number {
  const active = document.activeElement;
  return options.findIndex((o) => getMatOptionHostElement(o) === active);
}

function findNextEnabledIndex(
  options: MatOption[],
  from: number,
  direction: 1 | -1
): number {
  const len = options.length;
  if (len === 0) {
    return -1;
  }
  for (let step = 1; step <= len; step++) {
    const idx = (from + direction * step + len) % len;
    if (!options[idx].disabled) {
      return idx;
    }
  }
  return from;
}

function findLastEnabledIndex(options: MatOption[]): number {
  for (let i = options.length - 1; i >= 0; i--) {
    if (!options[i].disabled) {
      return i;
    }
  }
  return -1;
}

export type TabCommitDirection = 'forward' | 'backward';

/**
 * Arrow/Home/End navigation when DOM focus is on a mat-option (not the combobox trigger).
 * Optional `onTab` commits the highlighted option, closes the panel, and moves focus
 * forward (Tab) or backward (Shift+Tab).
 * Returns true when the key was handled.
 */
export function handleMatOptionListKeydown(
  event: KeyboardEvent,
  options: MatOption[],
  onActiveIndexChange: (index: number) => void,
  onTab?: (direction: TabCommitDirection) => void
): boolean {
  const key = event.key;
  if (key === 'Tab' && onTab) {
    event.preventDefault();
    event.stopPropagation();
    onTab(event.shiftKey ? 'backward' : 'forward');
    return true;
  }
  if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') {
    return false;
  }

  const enabledCount = options.filter((o) => !o.disabled).length;
  if (enabledCount === 0) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();

  let currentIndex = getFocusedMatOptionIndex(options);
  if (currentIndex < 0 || options[currentIndex]?.disabled) {
    currentIndex = options.findIndex((o) => !o.disabled);
  }

  let nextIndex = currentIndex;
  switch (key) {
    case 'ArrowDown':
      nextIndex = findNextEnabledIndex(options, currentIndex, 1);
      break;
    case 'ArrowUp':
      nextIndex = findNextEnabledIndex(options, currentIndex, -1);
      break;
    case 'Home':
      nextIndex = options.findIndex((o) => !o.disabled);
      break;
    case 'End':
      nextIndex = findLastEnabledIndex(options);
      break;
  }

  if (nextIndex >= 0) {
    onActiveIndexChange(nextIndex);
  }
  return true;
}

export type MatOptionListPanelBinding = {
  panelElement: HTMLElement | null;
  keydownHandler: ((event: KeyboardEvent) => void) | null;
};

export function attachMatOptionListPanelKeyboard(
  panelElement: HTMLElement,
  getOptions: () => MatOption[],
  onActiveIndexChange: (index: number) => void,
  onTab?: (direction: TabCommitDirection) => void
): MatOptionListPanelBinding {
  const keydownHandler = (event: KeyboardEvent) => {
    handleMatOptionListKeydown(
      event,
      getOptions(),
      (index) => {
        onActiveIndexChange(index);
        focusMatOptionElement(getOptions()[index]);
      },
      onTab
    );
  };
  panelElement.addEventListener('keydown', keydownHandler, true);
  return { panelElement, keydownHandler };
}

export function detachMatOptionListPanelKeyboard(
  binding: MatOptionListPanelBinding | null
): void {
  if (binding?.panelElement && binding.keydownHandler) {
    binding.panelElement.removeEventListener(
      'keydown',
      binding.keydownHandler,
      true
    );
  }
}
